import { Component, Input, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FeeBasisRowDto } from '../../services/invoice.service';

export type FeeBasisTableRow = FeeBasisRowDto & { sum: number; isSubtotal?: boolean; isSingletonGroup?: boolean };

@Component({
    selector: 'app-fee-basis-table',
    templateUrl: './fee-basis-table.component.html',
    styleUrls: ['./fee-basis-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FeeBasisTableComponent implements OnChanges {
  @Input() mode: 'konsulent' | 'opgave' = 'konsulent';
  @Input() identifier: string = '';
  @Input() rows: FeeBasisRowDto[] = [];

  dataSource = new MatTableDataSource<FeeBasisTableRow>([]);
  totals: { leadCreator: number; salesRealizator: number; executor: number; sum: number } = { leadCreator: 0, salesRealizator: 0, executor: 0, sum: 0 };

  isDataRow = (_: number, row: FeeBasisTableRow) => !row.isSubtotal;
  isSubtotalRow = (_: number, row: FeeBasisTableRow) => !!row.isSubtotal;

  private _sorter: MatSort;
  @ViewChild(MatSort)
  set sorter(s: MatSort) {
    this._sorter = s;
    if (s) this.attachSort();
  }
  get sorter(): MatSort { return this._sorter; }

  get displayedColumns(): string[] {
    const identifierColumn = this.mode === 'konsulent' ? 'taskNumber' : 'consultantInitials';
    return ['date', 'invoiceNumber', identifierColumn, 'customer', 'leadCreator', 'salesRealizator', 'executor', 'sum'];
  }

  ngOnChanges(_: SimpleChanges): void {
    const enriched: FeeBasisTableRow[] = (this.rows || []).map(r => ({
      ...r,
      sum: (r.leadCreator ?? 0) + (r.salesRealizator ?? 0) + (r.executor ?? 0)
    }));
    this.dataSource = new MatTableDataSource<FeeBasisTableRow>(enriched);
    this.attachSort();

    this.totals = {
      leadCreator: enriched.reduce((acc, r) => acc + (r.leadCreator ?? 0), 0),
      salesRealizator: enriched.reduce((acc, r) => acc + (r.salesRealizator ?? 0), 0),
      executor: enriched.reduce((acc, r) => acc + (r.executor ?? 0), 0),
      sum: enriched.reduce((acc, r) => acc + r.sum, 0)
    };
  }

  private attachSort(): void {
    const ds = this.dataSource;
    if (!ds) return;
    ds.sortingDataAccessor = (row, property) => {
      if (property === 'date') return row.date ? new Date(row.date).getTime() : 0;
      return (row as any)[property];
    };
    ds.sortData = (data: FeeBasisTableRow[], sort: MatSort): FeeBasisTableRow[] => {
      const active = sort.active;
      const direction = sort.direction;
      const rows = data.filter(r => !r.isSubtotal);
      if (!active || direction === '') return rows;
      const accessor = ds.sortingDataAccessor;
      const collator = new Intl.Collator('da', { numeric: true, sensitivity: 'base' });
      const compare = (a: FeeBasisTableRow, b: FeeBasisTableRow, key: string): number => {
        const av = accessor(a, key);
        const bv = accessor(b, key);
        if (typeof av === 'string' || typeof bv === 'string') {
          return collator.compare(String(av ?? ''), String(bv ?? ''));
        }
        return ((av as number) ?? 0) - ((bv as number) ?? 0);
      };
      const sorted = [...rows].sort((a, b) => {
        let cmp = compare(a, b, active);
        if (cmp === 0 && active === 'date') cmp = compare(a, b, 'invoiceNumber');
        return direction === 'asc' ? cmp : -cmp;
      });
      if (active !== 'date' && active !== 'invoiceNumber') return sorted;
      return this.injectSubtotals(sorted);
    };
    if (this._sorter) ds.sort = this._sorter;
  }

  private injectSubtotals(sorted: FeeBasisTableRow[]): FeeBasisTableRow[] {
    const result: FeeBasisTableRow[] = [];
    const flushGroup = (start: number, end: number) => {
      const group = sorted.slice(start, end);
      if (group.length > 1) {
        result.push(...group);
        result.push({
          isSubtotal: true,
          date: group[0].date,
          invoiceNumber: group[0].invoiceNumber,
          customer: 'Subtotal',
          leadCreator: group.reduce((s, r) => s + (r.leadCreator ?? 0), 0),
          salesRealizator: group.reduce((s, r) => s + (r.salesRealizator ?? 0), 0),
          executor: group.reduce((s, r) => s + (r.executor ?? 0), 0),
          sum: group.reduce((s, r) => s + (r.sum ?? 0), 0)
        } as FeeBasisTableRow);
      } else {
        result.push({ ...group[0], isSingletonGroup: true });
      }
    };
    let groupStart = 0;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].invoiceNumber !== sorted[i - 1].invoiceNumber) {
        flushGroup(groupStart, i);
        groupStart = i;
      }
    }
    if (sorted.length > 0) flushGroup(groupStart, sorted.length);
    return result;
  }
}
